import { LOCAL_STORAGE_OIDC_TOKEN_NAME } from '@constants';
import axios from 'axios';
// eslint-disable-next-line import/no-unresolved, camelcase
import * as jwt_decode from 'jwt-decode';
import { nanoid } from 'nanoid';
import showSnackbar from './apiSnackbar';
import { showNoticePopup } from './noticePopup';

const REDIRECT_OUT_OF_APP_ON_500 = false;

const apiClient = (baseUrl, lang) => {
  const axiosInstance = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    headers: {
      common: {
        'x-lang': lang.get(),
        'Accept-Language': lang.get(),
      },
    },
  });

  axiosInstance.interceptors.request.use(config => {
    let sessionState = null;
    const tokenName = window.localStorage.getItem(
      LOCAL_STORAGE_OIDC_TOKEN_NAME,
    );
    const sessionAuthData = window.localStorage.getItem(tokenName) || null;

    const token = sessionAuthData
      ? JSON.parse(sessionAuthData)?.access_token
      : null;

    if (token) {
      try {
        const decodedToken = jwt_decode(token);
        sessionState = decodedToken?.session_state || null;
      } catch {
        sessionState = null;
      }
    }

    const now = new Date().toISOString();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
      ...config,
      params: {
        ...config.params,
        _ts: Date.now(),
      },
      headers: {
        ...config.headers,
        requestid: nanoid(21),
        Authorization: `Bearer ${token}`,
        ...(sessionState && { sessionid: sessionState }),
        'X-Client-Datetime': now,
        'X-Client-Timezone': timezone,
      },
    };
  });

  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      if (error?.code === 'ERR_NETWORK' || error?.response?.status === 503) {
        const errorMessage = {
          message: encodeURIComponent(
            `Network error when accessing url ${error?.config?.url}`,
          ),
        };
        window.location.href = `/servicedown?networkError=${JSON.stringify(
          errorMessage,
        )}`;
      }
      if (error.response.status === 401) {
        window.location.href = '/unauthorized';
      }

      if (error.response.status === 403) {
        showSnackbar({
          type: 'error',
          errors:
            error?.response?.data?.errors ||
            'You do not have permission to perform this action',
        });
      }
      if (error.response.status === 409) {
        if (
          error?.response?.data?.errors &&
          error?.response?.data?.errors.find(
            e => e.errorCode === 'ConflictException',
          )
        ) {
          // No message: the popup renders its localized stale-data default.
          showNoticePopup(null, error?.response?.data?.errors);
        }
      }

      if (REDIRECT_OUT_OF_APP_ON_500 && error.response.status === 500) {
        const errorMessage = {
          message: encodeURIComponent(
            `Server error when accessing url ${error?.config?.url}`,
          ),
        };
        window.location.href = `/servererror?serverError=${JSON.stringify(
          errorMessage,
        )}`;
      }

      // 400 bad request return response
      return error.response;
    },
  );

  return axiosInstance;
};

export default apiClient;
